import React, { useState } from 'react';
import { Alert } from '@material-ui/lab';
import { UserService } from '../../services';
import { makeStyles } from '@material-ui/core/styles';
import { useNotification } from '../../contexts/NotificationContext';
import { humanizeSnakeCaseString } from '../../utils/formatters';
import { isSuccessfulResponse } from '../../utils/responseHelpers';
import { ROLE_PRESETS, isPresetSelected, togglePreset, isPresetLocked, lockingPresetName } from '../../utils/constants';
import { Modal, Button, TextField, Grid, Snackbar, Checkbox, FormControlLabel, FormGroup, FormLabel, FormControl, Tooltip } from '@material-ui/core';

function isBlank(value) {
    return value == null || value.trim().length === 0;
}

const useStyles = makeStyles((theme) => ({
    paper: {
        position: 'absolute',
        left: "20%",
        top: "15%",
        width: '50vw',
        height: '70vh',
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
        overflowY: 'scroll',
    },
    modalButton: {
        marginLeft: '15px',
    },
}));

const NewUser = (props) => {
    const classes = useStyles();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [roles, setRoles] = useState([]);

    const [emailError, setEmailError] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);

    const { createErrorNotification } = useNotification();
    const [showWaitMessage, setShowWaitMessage] = useState(false);

    const resetState = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setRoles([]);
        setEmailError(false);
        setFirstNameError(false);
        setLastNameError(false);
    };

    const handleRoleChange = (role) => {
        setRoles((prev) =>
            prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
        );
    };

    const handlePresetChange = (preset) => setRoles((prev) => togglePreset(preset, prev));

    const handleSubmit = async (event) => {
        event.preventDefault();

        let hasErrors = false;
        if (isBlank(email))     { setEmailError(true);     hasErrors = true; }
        if (isBlank(firstName)) { setFirstNameError(true); hasErrors = true; }
        if (isBlank(lastName))  { setLastNameError(true);  hasErrors = true; }

        if (hasErrors) {
            createErrorNotification("Missing Required Fields");
            return;
        }

        try {
            const response = await UserService.newUser({
                email_address: email,
                first_name: firstName,
                last_name: lastName,
                roles,
            });

            if (isSuccessfulResponse(response)) {
                resetState();
                props.toggleShowNewUser();
                setShowWaitMessage(true);
                setTimeout(() => window.location.reload(), 10000);
            } else {
                throw new Error("Failed to create new user");
            }
        } catch (err) {
            createErrorNotification("Failed to create new user");
        }
    };

    const handleCancel = () => {
        props.toggleShowNewUser();
        resetState();
    };

    const closeWaitMessage = () => setShowWaitMessage(false);

    const body = (
        <div className={classes.paper}>
            <h2 id="simple-modal-title">Create User</h2>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3} justifyContent="flex-start">
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="First Name"
                            fullWidth
                            required
                            autoFocus
                            error={firstNameError}
                            value={firstName}
                            onChange={(e) => { setFirstName(e.target.value); setFirstNameError(false); }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Last Name"
                            fullWidth
                            required
                            error={lastNameError}
                            value={lastName}
                            onChange={(e) => { setLastName(e.target.value); setLastNameError(false); }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            required
                            error={emailError}
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setEmailError(false); }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Permission Presets</FormLabel>
                            <FormGroup row>
                                {ROLE_PRESETS.map((preset) => {
                                    const locked = isPresetLocked(preset, roles);
                                    const label = (
                                        <FormControlLabel
                                            label={preset.name}
                                            control={
                                                <Checkbox
                                                    checked={isPresetSelected(preset, roles)}
                                                    disabled={locked}
                                                    onChange={() => handlePresetChange(preset)}
                                                />
                                            }
                                        />
                                    );
                                    if (!locked) return <span key={preset.name}>{label}</span>;
                                    const name = lockingPresetName(preset, roles);
                                    return (
                                        <Tooltip key={preset.name} title={name ? `Included in ${name}` : 'Included in another selected preset'}>
                                            <span>{label}</span>
                                        </Tooltip>
                                    );
                                })}
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Roles</FormLabel>
                            <FormGroup row>
                                {(props.roles || []).map((role) => (
                                    <FormControlLabel
                                        key={role}
                                        label={humanizeSnakeCaseString(role)}
                                        control={
                                            <Checkbox
                                                checked={roles.includes(role)}
                                                onChange={() => handleRoleChange(role)}
                                            />
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" className={classes.modalButton} onClick={handleSubmit}>Create</Button>
                        <Button variant="contained" className={classes.modalButton} onClick={handleCancel}>Cancel</Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    );

    return (
        <div>
            <Snackbar open={showWaitMessage} autoHideDuration={3000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={closeWaitMessage} severity="success">
                    {"Creating new user. This change will take some time to propagate. Please refresh the page in a few seconds to see the new user."}
                </Alert>
            </Snackbar>
            <Modal
                open={props.showNewUser}
                onClose={props.toggleShowNewUser}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description">
                {body}
            </Modal>
        </div>
    );
};

export default NewUser;
